import React from 'react'
import { MenuItem, ListSubheader, Typography } from '@mui/material'
import { TOTAL_EXPERIENCE } from 'services/freelancer'
import Tag from 'components/Tag'
import MoreButtonMenu from 'components/MoreButtonMenu'
import { ArrowDropDown } from '@mui/icons-material'
import { graphql, useFragment } from 'react-relay/hooks'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { ExperienceSummary_UpdateUserMutation } from '__generated__/ExperienceSummary_UpdateUserMutation.graphql'
import { ExperienceSummary_User$key } from '__generated__/ExperienceSummary_User.graphql'
import useStyles from './styles'

export interface IExperienceSummaryProps {
  editable: boolean
  user: ExperienceSummary_User$key
}

function ExperienceSummary(props: IExperienceSummaryProps) {
  const { classes } = useStyles()
  const { editable, user: userFragmentRef } = props
  const { showSnackbarMessage } = useSnackbar()

  const user = useFragment(graphql`
    fragment ExperienceSummary_User on User {
      profile {
        totalExperience
      }
    }
  `, userFragmentRef)

  const { execute } = useQuickCommit<ExperienceSummary_UpdateUserMutation>(graphql`
    mutation ExperienceSummary_UpdateUserMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          profile {
            totalExperience
          }
        }
      }
    }
  `)

  const onChange = (value: number) => {
    execute({
      input: {
        profile: {
          totalExperience: value,
        },
      },
    }).then((result) => {
      if (result?.updateUser?.user?.profile?.totalExperience) {
        showSnackbarMessage('Experience updated', { level: 'success' })
      }
    })
  }

  const totalExperience = user?.profile?.totalExperience
  const experienceObject = TOTAL_EXPERIENCE.find(x => x.value === totalExperience)

  const error = editable && (totalExperience ?? null) === null ? 'Total experience is required' : null

  if (!editable && !totalExperience) {
    return null
  }

  return (
    <React.Fragment>
      <Tag data-cy="profile-total-experience-container">
        <Typography variant="body1" component="span">
          {experienceObject ? experienceObject.label : 'Years of'} experience
        </Typography>

        {editable && (
          <MoreButtonMenu icon={<ArrowDropDown />} data-cy="profile-total-experience" className={classes.edit}>
            <ListSubheader>
              Years of Experience
            </ListSubheader>

            {TOTAL_EXPERIENCE.map(item => (
              <MenuItem
                value={item.value}
                key={item.value}
                data-cy={`total-experience-${item.value}`}
                onClick={() => onChange(item.value)}
              >
                {item.label}
              </MenuItem>
            ))}
          </MoreButtonMenu>
        )}

        {error && (
          <div className={classes.errorInline}>
            <Typography variant="body1" component="span">
              {error}
            </Typography>
          </div>
        )}
      </Tag>
    </React.Fragment>
  )
}

export default React.memo(ExperienceSummary)
